/**
 * Route-transition hook for the page-scoped analytics context (task 0061,
 * horizon issue 16 row G6) — see {@link ./pageContextAnalytics}.
 *
 * Lives at the storefront root route so every committed navigation — including
 * same-route category switches (PLP A → PLP B) whose page-data comes from the
 * TanStack Query cache with a stale object identity — is observed through the
 * router's location, not through loader-data identity. The page-scoped values
 * themselves come from the leaf match's page-data metadata (breadcrumbs) and,
 * on the search routes, the `text` search parameter (classic's
 * `freeTextSearch`).
 *
 * Push granularity mirrors classic's document re-render: a push happens when
 * the location path or the derived page context changes. Search-parameter-only
 * changes (pagination, sort, filter within the same result set) do not re-push
 * — classic never re-emitted for those either.
 */

import { useEffect, useRef } from "react";
import { useRouterState } from "@tanstack/react-router";
import type { PageMetadata } from "~/app/api/pageData";
import { applyPageAnalyticsContext, breadcrumbCategoryChain } from "./pageContextAnalytics";

/** Route ids whose document carried the free-text search keyword in classic. */
const SEARCH_KEYWORD_ROUTE_IDS = new Set(["/search", "/search/searchpage"]);

interface RouteMatchLike {
  routeId: string;
  loaderData: unknown;
}

function isPageMetadata(value: unknown): value is PageMetadata {
  return typeof value === "object" && value !== null && Array.isArray((value as PageMetadata).breadcrumbs);
}

/**
 * Reads the leaf page-data metadata from the committed matches. Route loaders
 * return either a bare `PageInitialData` (`.metadata`) or the product-route
 * wrapper (`.result.metadata`); routes without page-data yield null — classic
 * still emitted its (empty) page context on those documents, so null pushes
 * the empty context rather than skipping.
 */
function readLeafPageMetadata(matches: readonly RouteMatchLike[]): PageMetadata | null {
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const loaderData = matches[index].loaderData;
    if (typeof loaderData !== "object" || loaderData === null) {
      continue;
    }

    const bare = (loaderData as { metadata?: unknown }).metadata;
    if (isPageMetadata(bare)) {
      return bare;
    }

    const wrapped = (loaderData as { result?: { metadata?: unknown } }).result?.metadata;
    if (isPageMetadata(wrapped)) {
      return wrapped;
    }
  }

  return null;
}

/** Classic's `freeTextSearch`: the `text` parameter of the search routes, empty elsewhere. */
function readSearchKeyword(routeId: string, search: unknown): string {
  if (!SEARCH_KEYWORD_ROUTE_IDS.has(routeId)) {
    return "";
  }

  const text = (search as { text?: unknown } | undefined)?.text;
  return typeof text === "string" ? text : "";
}

export function useRoutePageAnalyticsContext(): void {
  const { status, pathname, search, matches } = useRouterState({
    select: (state) => ({
      status: state.status,
      pathname: state.location.pathname,
      search: state.location.search,
      matches: state.matches,
    }),
  }) as { status: "pending" | "idle"; pathname: string; search: unknown; matches: RouteMatchLike[] };

  const metadata = readLeafPageMetadata(matches);
  const breadcrumbChain = breadcrumbCategoryChain(metadata?.breadcrumbs);
  const routeId = matches.length > 0 ? matches[matches.length - 1].routeId : "";
  const searchKeyword = readSearchKeyword(routeId, search);

  const lastPushKeyRef = useRef<string | null>(null);
  const pushKey = `${pathname}|${breadcrumbChain}|${searchKeyword}`;

  useEffect(() => {
    // Only committed navigations emit: during a pending transition (boot,
    // in-flight loaders) the leaf match's loader data is not resolved yet and
    // the classic document equivalent — the per-document emission — happens
    // once, at render of the final page.
    if (status !== "idle" || lastPushKeyRef.current === pushKey) {
      return;
    }

    lastPushKeyRef.current = pushKey;
    applyPageAnalyticsContext({ breadcrumbChain, searchKeyword });
  }, [status, pushKey, breadcrumbChain, searchKeyword]);
}
